import { useEffect, useRef } from "react";

// Ambient dark mesh: soft gold aura that eases toward the cursor over a
// deep charcoal field, with slow drifting light nodes. Pure canvas, no deps.
export default function HeroCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, dpr;
    const pointer = { x: 0.5, y: 0.35, tx: 0.5, ty: 0.35 };

    const nodes = Array.from({ length: 5 }).map((_, i) => ({
      bx: 0.2 + Math.random() * 0.6,
      by: 0.2 + Math.random() * 0.6,
      r: 220 + Math.random() * 180,
      spd: 0.00012 + Math.random() * 0.0002,
      ph: Math.random() * Math.PI * 2,
      gold: i % 2 === 0,
    }));

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.tx = (e.clientX - rect.left) / rect.width;
      pointer.ty = (e.clientY - rect.top) / rect.height;
    };
    window.addEventListener("mousemove", onMove);

    let raf;
    const draw = (t) => {
      pointer.x += (pointer.tx - pointer.x) * 0.045;
      pointer.y += (pointer.ty - pointer.y) * 0.045;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#0d0d0d";
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = "lighter";
      nodes.forEach((n) => {
        const px = (n.bx + Math.sin(t * n.spd + n.ph) * 0.06) * w;
        const py = (n.by + Math.cos(t * n.spd * 0.8 + n.ph) * 0.06) * h;
        const g = ctx.createRadialGradient(px, py, 0, px, py, n.r);
        if (n.gold) {
          g.addColorStop(0, "rgba(212,175,55,0.10)");
          g.addColorStop(1, "rgba(212,175,55,0)");
        } else {
          g.addColorStop(0, "rgba(60,60,70,0.18)");
          g.addColorStop(1, "rgba(60,60,70,0)");
        }
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, n.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // cursor aura
      const cx = pointer.x * w;
      const cy = pointer.y * h;
      const ca = ctx.createRadialGradient(cx, cy, 0, cx, cy, 340);
      ca.addColorStop(0, "rgba(212,175,55,0.13)");
      ca.addColorStop(1, "rgba(212,175,55,0)");
      ctx.fillStyle = ca;
      ctx.beginPath();
      ctx.arc(cx, cy, 340, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="hero-bg-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ display: "block" }}
    />
  );
}
